// Civilian life & living airbases (roadmap #42 + #43): client-only ambient
// scenery driven entirely by (world.seed, serverTime) so every client sees the
// same boats and whales at the same instant with ZERO wire traffic —
// the same determinism trick buildClouds uses. Nothing here shoots, scores, or
// collides, so a client with it hidden and one with it shown run identical
// simulations. The moment any of it becomes shootable (the Gold Train, Theme B
// #10) it moves server-side; this rendering is reused as-is.
//
// Reactive motion (gull scatter, ground-crew scatter) is deliberately NOT
// synced across clients — only the deterministic anchors are. Cosmetic
// divergence there is fine and safe.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";
import { mergeBoxes, TEAM_INFO, CLASS_BUILDERS } from "./planes.js";
import { teamBasePos } from "./world.js";
import { bakedGeometry, loadModel, modelsEnabled, settle } from "./models.js";
import { LOD_DIST, loadLevels, lodOf } from "./unit_models.js";
import { GFX } from "./gfx.js";
import { touchInstances } from "./instance_upload.js";

// One shared unlit material for scene-lit voxel scenery (matches the terrain
// material's flat look without importing it).
const litMat = new THREE.MeshLambertMaterial({ vertexColors: true });

const TAU = Math.PI * 2;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

// The air base's buildings (art/airbase/, model-integration.md Part 7), laid
// out in the apron frame: lx down the runway, lz toward the HQ. `yaw` turns a
// model's +X front — hangar doors, tower door, barracks porch — so −π/2 faces
// the apron from the map-centre side and +π/2 faces the strip from the HQ
// side. The reserve planes park in their revetments along lz +32 (aaa.go
// placeParkedAircraft; the revetments ride with them in AAGuns), so nothing
// here stands in lz 16–48 over lx ±52.
const HANGAR_SLOTS = [[-30, -40], [22, -40]]; // doors to the apron, pads at lz −22
const TOWER_SLOT = [-80, -34];
const AIRBASE_DRESSING = [
  // Two barracks side by side behind the east hangar, gable porches (and
  // the team sign board) turned to the apron.
  { stem: "barracks", lx: 55, lz: -44, yaw: -Math.PI / 2 },
  { stem: "barracks", lx: 67, lz: -44, yaw: -Math.PI / 2 },
  // Quonset huts — stores and the ready room — out toward the east corner.
  { stem: "quonset_hut", lx: 80, lz: -48, yaw: -Math.PI / 2 },
  { stem: "quonset_hut", lx: 90, lz: -48, yaw: -Math.PI / 2 },
  // The water tower: after the control tower, the tallest thing on the field.
  { stem: "water_tower", lx: -62, lz: -52, yaw: 0 },
  // Earth-covered ammunition bunker, blast doors to the strip, well clear of
  // the revetments and the HQ.
  { stem: "ammo_bunker", lx: -75, lz: 40, yaw: Math.PI / 2 },
  // Pillboxes flanking the runway ends, looking out.
  { stem: "pillbox", lx: 95, lz: 22, yaw: 0 },
  { stem: "pillbox", lx: 95, lz: -22, yaw: 0 },
  { stem: "pillbox", lx: -95, lz: 22, yaw: Math.PI },
  { stem: "pillbox", lx: -95, lz: -22, yaw: Math.PI },
];
// The hangar's box stand-in, in the model's frame (doors +X): shell, arched
// roof cap, ridge, dark door opening and the team band over it.
function hangarBoxes(info) {
  return [
    { x: 0, y: 6, z: 0, w: 20, h: 12, d: 26, color: 0x8a8f8c },
    { x: 0, y: 12.6, z: 0, w: 21, h: 2.2, d: 27, color: 0x6f746f },
    { x: 0, y: 13.9, z: 0, w: 15, h: 1.2, d: 20, color: 0x656a65 },
    { x: 10.2, y: 5, z: 0, w: 0.5, h: 10, d: 20, color: 0x2b2f33 },
    { x: 10.4, y: 10.6, z: 0, w: 0.4, h: 1.0, d: 21, color: info.color },
  ];
}

export class AmbientLife {
  constructor(scene, field, config, particles = null, audio = null) {
    this.scene = scene;
    this.field = field;
    this.particles = particles;
    this.audio = audio;
    this.cfg = config.ambient ?? {};
    this.baseCfg = config.ambientBase ?? {};
    this.t = 0;
    this.tmp = new THREE.Vector3();
    this.dummy = new THREE.Object3D();
    this.whaleSeenSlot = -1; // analytics dedupe: one "whale_seen" per surfacing
    this.onWhaleSeen = null; // main.js hook for the analytics event
    this.gullCryAt = 0;

    this._buildBoats();
    this._buildLighthouse();
    this._buildGulls();
    this._buildWhales();
    this._buildBases();
  }

  // --- determinism helpers --------------------------------------------------
  h(i, salt) {
    return hash2(i | 0, salt | 0, this.field.seed);
  }
  isWater(x, z) {
    return this.field.surfaceAt(x, z) <= this.field.waterLevel + 0.5;
  }

  // ==========================================================================
  // #42 Fishing boats — grey neutral hulls puttering radial water lanes.
  // ==========================================================================
  _buildBoats() {
    const n = this.cfg.fishingBoats ?? 0;
    this.boats = [];
    if (n <= 0) return;
    const geo = this._boatGeometry();
    for (let i = 0; i < n; i++) {
      const lane = this._findWaterLane(i);
      if (!lane) continue;
      const mesh = new THREE.Mesh(geo, litMat);
      this.scene.add(mesh);
      this.boats.push({
        mesh, ...lane,
        phase: this.h(i, 8101) * lane.len * 2, // stagger along the ping-pong
        bob: this.h(i, 8102) * TAU,
        smokeAt: this.h(i, 8103),
      });
    }
  }

  // A cut-down freighter: a small grey hull, wheelhouse, and stubby funnel.
  _boatGeometry() {
    const hull = 0x6b7079, hullDark = 0x4c525a, house = 0x9aa0a8;
    const glass = 0x8fb8cc, funnel = 0x3a3e45, rail = 0x2b2f36;
    return mergeBoxes([
      { x: 0, y: 0.7, z: 0, w: 13, h: 2.0, d: 3.4, color: hull },
      { x: 0, y: -0.6, z: 0, w: 13, h: 1.2, d: 3.7, color: hullDark },
      { x: 5.4, y: 1.3, z: 0, w: 2.6, h: 1.4, d: 2.6, color: hull }, // raised bow
      { x: -1.0, y: 2.1, z: 0, w: 4.0, h: 1.8, d: 3.0, color: house }, // wheelhouse
      { x: -0.4, y: 2.5, z: 0, w: 0.5, h: 0.9, d: 2.4, color: glass },
      { x: -3.4, y: 2.6, z: 0, w: 1.3, h: 1.8, d: 1.3, color: funnel },
      { x: -3.4, y: 3.6, z: 0, w: 1.5, h: 0.4, d: 1.5, color: 0x24272c },
      { x: 3.0, y: 1.5, z: 0, w: 0.2, h: 2.4, d: 0.2, color: rail }, // mast
    ]);
  }

  // Re-do of the server's findSupplyLane on the client: hash candidate radial
  // lanes offshore and keep the first that stays over open water end to end.
  _findWaterLane(i) {
    const R = this.field.radius;
    for (let a = 0; a < 8; a++) {
      const bearing = this.h(i * 8 + a, 8201) * TAU;
      const r0 = R * (0.55 + this.h(i * 8 + a, 8202) * 0.12);
      const r1 = R * (0.78 + this.h(i * 8 + a, 8203) * 0.12);
      const spread = (this.h(i * 8 + a, 8204) - 0.5) * 0.8;
      const p0 = { x: Math.cos(bearing) * r0, z: Math.sin(bearing) * r0 };
      const p1 = { x: Math.cos(bearing + spread) * r1, z: Math.sin(bearing + spread) * r1 };
      let ok = true;
      for (let s = 0; s <= 6; s++) {
        const f = s / 6;
        if (!this.isWater(p0.x + (p1.x - p0.x) * f, p0.z + (p1.z - p0.z) * f)) {
          ok = false;
          break;
        }
      }
      if (ok) {
        const len = Math.hypot(p1.x - p0.x, p1.z - p0.z);
        return { p0, p1, len, speed: this.cfg.boatSpeedMS ?? 4 };
      }
    }
    return null;
  }

  _updateBoats(dt) {
    const water = this.field.waterLevel;
    for (const b of this.boats) {
      // Ping-pong scalar distance along the lane (deterministic from svrT).
      const cycle = 2 * b.len;
      let s = ((this.t * b.speed + b.phase) % cycle + cycle) % cycle;
      const fwd = s <= b.len;
      const f = fwd ? s / b.len : 2 - s / b.len;
      const x = b.p0.x + (b.p1.x - b.p0.x) * f;
      const z = b.p0.z + (b.p1.z - b.p0.z) * f;
      const ph = this.t * 0.9 + b.bob;
      b.mesh.position.set(x, water + Math.sin(ph) * 0.25, z);
      const dir = fwd ? 1 : -1;
      b.mesh.rotation.y = Math.atan2(-(b.p1.z - b.p0.z) * dir, (b.p1.x - b.p0.x) * dir);
      b.mesh.rotation.z = Math.sin(ph * 0.7) * 0.03;
      // A thin diesel plume off the funnel now and then.
      if (this.particles) {
        b.smokeAt -= dt;
        if (b.smokeAt <= 0) {
          b.smokeAt = 0.5;
          const yaw = b.mesh.rotation.y;
          this.tmp.set(x - 3.4 * Math.cos(yaw), water + 4.2, z + 3.4 * Math.sin(yaw));
          this.particles.puff(
            this.tmp,
            { size: 1.6, grow: 1.8, speed: 0.7, alpha: 0.55, tint: [0.95, 0.95, 0.96], lift: 1.5, rise: 0.8 },
            {
              count: 1, speed: 2.2, life: 2.6, size: 0.6,
              colors: [0x9a9a9a, 0xb4b4b4, 0x868686], gravity: -2.5, drag: 0.8, grow: true,
            },
          );
        }
      }
    }
  }

  // ==========================================================================
  // #42 Lighthouse — a striped coastal tower whose beam sweeps at night.
  // ==========================================================================
  _buildLighthouse() {
    this.lighthouse = null;
    const R = this.field.radius;
    // Highest coastal column in the 0.85–0.95 R shore band (hashed search).
    let best = null, bestH = -1e9;
    for (let i = 0; i < 240; i++) {
      const ang = this.h(i, 8301) * TAU;
      const r = R * (0.85 + this.h(i, 8302) * 0.1);
      const x = Math.cos(ang) * r, z = Math.sin(ang) * r;
      const h = this.field.surfaceAt(x, z);
      if (h > this.field.waterLevel + 3 && h > bestH) {
        bestH = h;
        best = { x, z, y: h };
      }
    }
    if (!best) return;
    const white = 0xe8e6df, red = 0xc23a34, glass = 0xfff3c4, cap = 0x2b2f36;
    const tower = new THREE.Mesh(
      mergeBoxes([
        { x: 0, y: 2, z: 0, w: 9, h: 4, d: 9, color: 0x74706a }, // rock base
        { x: 0, y: 9, z: 0, w: 5.4, h: 10, d: 5.4, color: white },
        { x: 0, y: 6, z: 0, w: 5.7, h: 3, d: 5.7, color: red }, // barber stripes
        { x: 0, y: 12, z: 0, w: 5.7, h: 3, d: 5.7, color: red },
        { x: 0, y: 15.4, z: 0, w: 6.2, h: 1.2, d: 6.2, color: cap }, // gallery
        { x: 0, y: 17.2, z: 0, w: 4.2, h: 2.6, d: 4.2, color: glass }, // lantern room
        { x: 0, y: 19.2, z: 0, w: 5.0, h: 1.4, d: 5.0, color: cap }, // roof
      ]),
      litMat,
    );
    tower.position.set(best.x, best.y, best.z);
    this.scene.add(tower);
    // The rotating beam reuses the searchlight beam recipe: an additive cone
    // pointing down local +X, spun by the lantern head.
    const head = new THREE.Group();
    head.position.set(best.x, best.y + 17.5, best.z);
    this.scene.add(head);
    const beamGeo = new THREE.CylinderGeometry(24, 0.8, 900, 10, 1, true);
    beamGeo.rotateZ(-Math.PI / 2);
    beamGeo.translate(450, 0, 0);
    const beam = new THREE.Mesh(
      beamGeo,
      new THREE.MeshBasicMaterial({
        color: 0xfff3c4, transparent: true, opacity: 0.12,
        blending: THREE.AdditiveBlending, depthWrite: false, fog: false, side: THREE.DoubleSide,
      }),
    );
    beam.rotation.z = 0.02; // a hair below horizontal
    beam.visible = false;
    head.add(beam);
    // A warm lamp glow at the lantern, lit at night.
    const lamp = new THREE.Mesh(
      new THREE.BoxGeometry(2.2, 2.2, 2.2),
      new THREE.MeshBasicMaterial({ color: 0xfff0b0, transparent: true, opacity: 0.9, fog: false }),
    );
    lamp.position.copy(head.position);
    lamp.visible = false;
    this.scene.add(lamp);
    this.lighthouse = { head, beam, lamp };
  }

  _updateLighthouse(sky) {
    const l = this.lighthouse;
    if (!l) return;
    const night = sky?.nightK ?? 0;
    const on = night > 0.35;
    l.beam.visible = on;
    l.lamp.visible = on;
    if (on) {
      l.head.rotation.y = this.t * 0.5;
      l.beam.material.opacity = 0.08 + 0.08 * night;
    }
  }

  // ==========================================================================
  // #42 Seagull flocks — boids around hashed coastal anchors, one InstancedMesh.
  // ==========================================================================
  _buildGulls() {
    const flocks = this.cfg.gullFlocks ?? 0;
    this.gulls = null;
    if (flocks <= 0) return;
    const perFlock = 12;
    const total = flocks * perFlock;
    const birdGeo = mergeBoxes([
      { x: 0, y: 0, z: 0.9, w: 0.5, h: 0.14, d: 1.6, color: 0xf2f2f2 },
      { x: 0, y: 0, z: -0.9, w: 0.5, h: 0.14, d: 1.6, color: 0xf2f2f2 },
      { x: 0.5, y: 0.05, z: 0, w: 0.8, h: 0.24, d: 0.4, color: 0xdedede },
    ]);
    const mesh = new THREE.InstancedMesh(
      birdGeo,
      new THREE.MeshLambertMaterial({ color: 0xffffff }),
      total,
    );
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    this.scene.add(mesh);
    const R = this.field.radius;
    const anchors = [];
    for (let fi = 0; fi < flocks; fi++) {
      // Coastal anchor: sample the shore band for a point just above the water.
      let a = null;
      for (let s = 0; s < 60 && !a; s++) {
        const ang = this.h(fi * 60 + s, 8401) * TAU;
        const r = R * (0.4 + this.h(fi * 60 + s, 8402) * 0.5);
        const x = Math.cos(ang) * r, z = Math.sin(ang) * r;
        const h = this.field.surfaceAt(x, z);
        if (h <= this.field.waterLevel + 6) a = { x, z };
      }
      if (!a) a = { x: Math.cos(fi) * R * 0.6, z: Math.sin(fi) * R * 0.6 };
      anchors.push({ ...a, y: this.field.waterLevel + 40 + this.h(fi, 8403) * 30, flee: 0 });
    }
    const birds = [];
    for (let fi = 0; fi < flocks; fi++) {
      for (let bi = 0; bi < perFlock; bi++) {
        const g = this.h(fi * perFlock + bi, 8404);
        const g2 = this.h(fi * perFlock + bi, 8405);
        birds.push({
          flock: fi,
          pos: new THREE.Vector3(
            anchors[fi].x + (g - 0.5) * 60,
            anchors[fi].y + (g2 - 0.5) * 20,
            anchors[fi].z + (this.h(fi * perFlock + bi, 8406) - 0.5) * 60,
          ),
          vel: new THREE.Vector3((g - 0.5) * 8, 0, (g2 - 0.5) * 8),
          phase: g * TAU,
        });
      }
    }
    this.gulls = { mesh, anchors, birds, perFlock };
  }

  _updateGulls(dt, probes) {
    const G = this.gulls;
    if (!G) return;
    // Scatter: a fast low plane near a flock anchor sends the whole flock
    // fleeing for 6 s. Reactive only — never synced.
    for (const a of G.anchors) {
      if (a.flee > 0) a.flee -= dt;
      for (const p of probes) {
        const dx = p.x - a.x, dz = p.z - a.z;
        if (p.spd > 45 && dx * dx + dz * dz < 120 * 120 && Math.abs(p.y - a.y) < 90) {
          a.flee = 6;
          break;
        }
      }
    }
    const sep = new THREE.Vector3();
    for (const b of G.birds) {
      const a = G.anchors[b.flock];
      // Cohesion toward the anchor, gentle bob, light separation.
      b.vel.x += (a.x - b.pos.x) * 0.15 * dt;
      b.vel.z += (a.z - b.pos.z) * 0.15 * dt;
      b.vel.y += (a.y - b.pos.y) * 0.2 * dt;
      sep.set(0, 0, 0);
      for (const o of G.birds) {
        if (o === b || o.flock !== b.flock) continue;
        const dx = b.pos.x - o.pos.x, dy = b.pos.y - o.pos.y, dz = b.pos.z - o.pos.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < 36 && d2 > 1e-3) {
          const inv = 1 / d2;
          sep.x += dx * inv; sep.y += dy * inv; sep.z += dz * inv;
        }
      }
      b.vel.addScaledVector(sep, 12 * dt);
      if (a.flee > 0) {
        // Radial flee impulse away from the anchor centre.
        b.vel.x += (b.pos.x - a.x) * 0.6 * dt;
        b.vel.z += (b.pos.z - a.z) * 0.6 * dt;
        b.vel.y += 6 * dt;
      }
      // Speed clamp + a little wander so the flock never freezes.
      b.phase += dt * 3;
      b.vel.x += Math.cos(b.phase) * 2 * dt;
      b.vel.z += Math.sin(b.phase * 1.3) * 2 * dt;
      const sp = b.vel.length();
      const max = a.flee > 0 ? 34 : 16;
      if (sp > max) b.vel.multiplyScalar(max / sp);
      b.pos.addScaledVector(b.vel, dt);
      if (b.pos.y < this.field.waterLevel + 8) b.pos.y = this.field.waterLevel + 8;
    }
    // Write instance matrices: banked toward the flight direction, wings flapping.
    let idx = 0;
    for (const b of G.birds) {
      this.dummy.position.copy(b.pos);
      this.dummy.rotation.set(Math.sin(b.phase) * 0.35, Math.atan2(-b.vel.z, b.vel.x), 0);
      this.dummy.updateMatrix();
      G.mesh.setMatrixAt(idx++, this.dummy.matrix);
    }
    touchInstances(G.mesh.instanceMatrix, 0, idx);
  }

  // ==========================================================================
  // #42 Whales — a deterministic surfacing schedule, a dark back + a spout.
  // ==========================================================================
  _buildWhales() {
    const n = this.cfg.whales ?? 0;
    this.whales = [];
    if (n <= 0) return;
    const geo = mergeBoxes([
      { x: 0, y: 0, z: 0, w: 14, h: 2.6, d: 4.4, color: 0x2b3138 },
      { x: 5.5, y: -0.2, z: 0, w: 4, h: 1.8, d: 2.6, color: 0x2b3138 }, // head
      { x: -7.2, y: 0.6, z: 0, w: 2.2, h: 0.5, d: 5.2, color: 0x232a30 }, // flukes
      { x: 1, y: 1.2, z: 0, w: 2.2, h: 1.2, d: 0.4, color: 0x333b42 }, // dorsal hump
    ]);
    for (let i = 0; i < n; i++) {
      const mesh = new THREE.Mesh(geo, litMat);
      mesh.visible = false;
      this.scene.add(mesh);
      this.whales.push({ mesh, i, spoutAt: 0 });
    }
    this.whaleSlot = this.cfg.whaleSlotSec ?? 90;
    this.whaleChance = this.cfg.whaleChance ?? 0.15;
  }

  // A hashed open-water point at least 500 m offshore for whale i in slot n.
  _whaleSpot(i, n) {
    const R = this.field.radius;
    for (let s = 0; s < 40; s++) {
      const ang = hash2(i * 40 + s, n, (this.field.seed + 8501) >>> 0) * TAU;
      const r = R * (0.5 + hash2(i * 40 + s, n, (this.field.seed + 8502) >>> 0) * 0.4);
      const x = Math.cos(ang) * r, z = Math.sin(ang) * r;
      if (
        this.isWater(x, z) &&
        this.isWater(x + 500, z) && this.isWater(x - 500, z) &&
        this.isWater(x, z + 500) && this.isWater(x, z - 500)
      ) {
        return { x, z, yaw: ang };
      }
    }
    return null;
  }

  _updateWhales(dt, localPos) {
    if (!this.whales.length) return;
    const slot = Math.floor(this.t / this.whaleSlot);
    const water = this.field.waterLevel;
    for (const wh of this.whales) {
      const roll = hash2(wh.i, slot, (this.field.seed + 8503) >>> 0);
      const active = roll < this.whaleChance;
      if (!active) {
        wh.mesh.visible = false;
        continue;
      }
      const spot = this._whaleSpot(wh.i, slot);
      if (!spot) {
        wh.mesh.visible = false;
        continue;
      }
      // Surface over ~8 s within the slot: rise, arc, and slip back under.
      const into = this.t - slot * this.whaleSlot;
      const dur = 8;
      if (into > dur) {
        wh.mesh.visible = false;
        continue;
      }
      const f = into / dur; // 0..1
      const arc = Math.sin(f * Math.PI); // up and back down
      wh.mesh.visible = true;
      wh.mesh.position.set(spot.x, water - 2.2 + arc * 3.2, spot.z);
      wh.mesh.rotation.set(-arc * 0.25, spot.yaw, 0);
      // A white spout at the top of the arc + an analytics ping if the local
      // plane is close enough to have seen it.
      if (this.particles && f > 0.4 && f < 0.62) {
        wh.spoutAt -= dt;
        if (wh.spoutAt <= 0) {
          wh.spoutAt = this.particles.soft ? 0.3 : 0.12; // one column plays for seconds
          this.tmp.set(spot.x + 5.5 * Math.cos(spot.yaw), water + 3, spot.z - 5.5 * Math.sin(spot.yaw));
          this.particles.spray(this.tmp, 6, {
            count: 2, speed: 9, life: 1.6, size: 0.9,
            colors: [0xffffff, 0xeef7fc, 0xd5ecfa], gravity: 10, drag: 0.5,
            dir: new THREE.Vector3(0, 1, 0), spread: 0.25,
          });
        }
      }
      if (this.whaleSeenSlot !== slot && localPos) {
        const dx = localPos.x - spot.x, dz = localPos.z - spot.z;
        if (dx * dx + dz * dz < 400 * 400) {
          this.whaleSeenSlot = slot;
          this.onWhaleSeen?.();
        }
      }
    }
  }

  // ==========================================================================
  // #43 Living airbases — control tower, windsocks, ground crew per base.
  // ==========================================================================
  // The graded airfield apron — MUST mirror World.airfield() in world.go so
  // the deck the client draws is the deck the server parks aircraft on.
  airfield(bx, bz) {
    const inward = Math.atan2(-bz, -bx);
    const ix = Math.cos(inward), iz = Math.sin(inward);
    const tx = -iz, tz = ix;
    const cx = bx + ix * 70, cz = bz + iz * 70;
    let y = this.field.waterLevel;
    for (let a = -90; a <= 90; a += 30) {
      for (let b = -50; b <= 50; b += 25) {
        const h = this.field.surfaceAt(cx + tx * a + ix * b, cz + tz * a + iz * b);
        if (h > y) y = h;
      }
    }
    return { cx, cz, y: y + 1, ix, iz, tx, tz };
  }

  _buildBases() {
    this.bases = [];
    for (let team = 0; team < 2; team++) {
      const { x: bx, z: bz } = teamBasePos(this.field, team);
      const af = this.airfield(bx, bz);
      const base = { team, x: bx, z: bz, ground: af.y, af };
      this._buildApron(base, af, TEAM_INFO[team]);
      this._buildTower(base, af, TEAM_INFO[team]);
      if (this.baseCfg.windsocks !== false) this._buildWindsock(base, af);
      this._buildDressing(base);
      this._buildCrew(base, af);
      this.bases.push(base);
    }
  }

  // Local apron frame → world XZ. lx runs down the runway (tangential), lz runs
  // from the apron centre toward the HQ (−inward).
  _lw(af, lx, lz) {
    return { x: af.cx + af.tx * lx - af.ix * lz, z: af.cz + af.tz * lx - af.iz * lz };
  }

  // A building on the apron: a slot at (lx, lz) in the apron frame turned
  // `yaw`, holding its box stand-in (if it has one) until the model lands,
  // then the model's LOD with the box kept as the far level. `onModel` gets
  // the LOD's ModelInstances.
  _slot(base, stem, lx, lz, yaw, box = null, onModel = null) {
    const slot = new THREE.Group();
    slot.position.set(lx, 0, lz);
    slot.rotation.y = yaw;
    if (box) slot.add(box);
    base.apron.add(slot);
    loadLevels(stem).then((tpls) => {
      if (!tpls || !slot.parent) return;
      const { lod, levels } = lodOf(tpls, { team: base.team, dists: LOD_DIST.building, far: box });
      slot.add(lod);
      onModel?.(levels);
    });
    return slot;
  }

  // The apron slab, its skirts, the runway, and two hangars — all built in the
  // local runway frame and rotated into place as one group.
  _buildApron(base, af, info) {
    const g = new THREE.Group();
    g.position.set(af.cx, af.y, af.cz);
    // Rotate local +X onto the runway (tangential) direction.
    g.rotation.y = Math.atan2(-af.tz, af.tx);
    const concrete = 0x9a9a94, apronEdge = 0x7f7f79, asphalt = 0x3b3d40;
    const skirt = 0x6b6b66;
    const B = [];
    // Deck slab (top at local y=0) plus a skirt dropping 60 m to hide the gap
    // down to the terrain (or the sea, for a coastal base).
    B.push({ x: 0, y: -1, z: 0, w: 200, h: 2, d: 118, color: concrete });
    B.push({ x: 0, y: -0.3, z: 0, w: 202, h: 0.8, d: 120, color: apronEdge });
    B.push({ x: 0, y: -31, z: 59, w: 202, h: 60, d: 3, color: skirt });
    B.push({ x: 0, y: -31, z: -59, w: 202, h: 60, d: 3, color: skirt });
    B.push({ x: 100, y: -31, z: 0, w: 3, h: 60, d: 120, color: skirt });
    B.push({ x: -100, y: -31, z: 0, w: 3, h: 60, d: 120, color: skirt });
    // Runway strip down the middle, threshold bars, dashed centreline.
    B.push({ x: 0, y: 0.15, z: 0, w: 168, h: 0.3, d: 26, color: asphalt });
    for (const end of [-80, 80]) {
      for (let k = -4; k <= 4; k++) {
        B.push({ x: end, y: 0.32, z: k * 2.4, w: 4, h: 0.12, d: 1.4, color: 0xe8e8e0 });
      }
    }
    for (let x = -70; x <= 70; x += 14) {
      B.push({ x, y: 0.32, z: 0, w: 6, h: 0.12, d: 1.0, color: 0xe8e8e0 });
    }
    const deck = new THREE.Mesh(mergeBoxes(B), litMat);
    deck.receiveShadow = GFX >= 2; // tier 2's sun shadow: hangars and parked planes on the concrete
    g.add(deck);
    this.scene.add(g);
    // The apron and everything on it — hangars, tower, the station's huts —
    // stand still: the walk places each piece once, as it lands (models.js
    // WorldScene). The windsock streams, so it stands apart (_buildWindsock).
    settle(g);
    base.apron = g;
    // Two hangars along the far (map-centre) edge, doors facing the apron and
    // the team band over them: art/airbase/hangar.glb, 35 m across with its
    // flank workshops, the arched-roof boxes as its stand-in.
    for (const [hx, hz] of HANGAR_SLOTS) {
      const box = new THREE.Mesh(mergeBoxes(hangarBoxes(info)), litMat);
      this._slot(base, "hangar", hx, hz, -Math.PI / 2, box);
    }
  }

  _buildTower(base, af, info) {
    // Control tower at one corner of the apron (map-centre side, off the
    // runway): a stack with a glass cab, a lit window band that comes up at
    // dusk, and a blinking red beacon. art/airbase/control_tower.glb takes the
    // stack's place, door to the apron; its cab and beacon sit lower, so the
    // band and beacon move down to meet them when it lands.
    const wall = 0x8b8f94, dark = 0x5a5e63, glass = 0x2a3238;
    const tower = new THREE.Mesh(
      mergeBoxes([
        { x: 0, y: 6, z: 0, w: 10, h: 12, d: 10, color: wall },
        { x: 0, y: 12.6, z: 0, w: 10.6, h: 1.4, d: 10.6, color: info.color },
        { x: 0, y: 16, z: 0, w: 8, h: 5, d: 8, color: dark },
        { x: 0, y: 18.4, z: 0, w: 8.6, h: 3.0, d: 8.6, color: glass },
        { x: 0, y: 20.4, z: 0, w: 9.0, h: 1.2, d: 9.0, color: dark },
        { x: 0, y: 22.2, z: 0, w: 0.6, h: 2.4, d: 0.6, color: 0x2b2f36 },
      ]),
      litMat,
    );
    const lit = new THREE.Mesh(
      new THREE.BoxGeometry(8.8, 3.0, 8.8),
      new THREE.MeshBasicMaterial({ color: 0xffdf9a, transparent: true, opacity: 0.55, fog: false }),
    );
    lit.position.set(0, 18.4, 0);
    lit.visible = false;
    const beacon = new THREE.Mesh(
      new THREE.BoxGeometry(1.0, 1.0, 1.0),
      new THREE.MeshBasicMaterial({ color: 0xff3b30, transparent: true, opacity: 0.95, fog: false }),
    );
    beacon.position.set(0, 23.6, 0);
    const slot = this._slot(base, "control_tower", TOWER_SLOT[0], TOWER_SLOT[1], -Math.PI / 2, tower, () => {
      // The model's glass cab runs 8.3–11.4 m up; its beacon tops out at 16.
      lit.scale.set(7.0 / 8.8, 3.1 / 3.0, 7.0 / 8.8);
      lit.position.y = 9.85;
      beacon.position.y = 15.75;
    });
    slot.add(lit, beacon);
    base.tower = { lit, beacon };
  }

  _buildWindsock(base, af) {
    // A pole + 3-segment sock by a runway threshold; points downwind, inflates.
    // Stands in world axes (not the apron's) so a sock's yaw is the wind's.
    // art/airbase/windsock.glb's ABS_Sock pivots at the same 7.6 m and
    // streams along +X like the boxes, so one loop drives every level.
    const p = this._lw(af, 80, 40);
    const at = new THREE.Group();
    at.position.set(p.x, af.y, p.z);
    this.scene.add(at);
    const box = new THREE.Group();
    box.add(new THREE.Mesh(
      mergeBoxes([{ x: 0, y: 4, z: 0, w: 0.4, h: 8, d: 0.4, color: 0x8a8f94 }]),
      litMat,
    ));
    const sock = new THREE.Group();
    sock.position.set(0, 7.6, 0);
    const seg = (x, r, color) =>
      new THREE.Mesh(mergeBoxes([{ x, y: 0, z: 0, w: 1.4, h: r, d: r, color }]), litMat);
    sock.add(seg(1.0, 1.6, 0xff7a2a));
    sock.add(seg(2.4, 1.2, 0xf2f2f2));
    sock.add(seg(3.8, 0.8, 0xff7a2a));
    box.add(sock);
    at.add(box);
    base.windsock = { socks: [sock] };
    loadLevels("windsock").then((tpls) => {
      if (!tpls) return;
      const { lod, levels } = lodOf(tpls, { team: base.team, dists: LOD_DIST.building, far: box });
      at.add(lod);
      for (const lv of levels) {
        const s = lv.node("Sock");
        if (s) base.windsock.socks.push(s);
      }
    });
  }

  // The rest of the station: barracks, huts, water tower, bunker and
  // pillboxes (AIRBASE_DRESSING). Models only — there is no box version to
  // fall back to, so under ?boxes the field is its hangars, tower and sock.
  _buildDressing(base) {
    if (!modelsEnabled()) return;
    for (const d of AIRBASE_DRESSING) this._slot(base, d.stem, d.lx, d.lz, d.yaw);
  }

  _buildCrew(base, af) {
    const n = this.baseCfg.groundCrew ?? 7;
    if (n <= 0) {
      base.crew = null;
      return;
    }
    const geo = mergeBoxes([
      { x: 0, y: 0.9, z: 0, w: 0.6, h: 1.8, d: 0.6, color: 0x3f5a3a }, // body
      { x: 0, y: 2.1, z: 0, w: 0.5, h: 0.5, d: 0.5, color: 0xcaa07a }, // head
    ]);
    // vertexColors so the uniform-green body / skin head show (gulls stay white).
    const mesh = new THREE.InstancedMesh(geo, litMat, n);
    mesh.frustumCulled = false;
    this.scene.add(mesh);
    // The ground-crew figure (art/ordnance/ground_crew.glb): overalls, cap and
    // boots on the same instanced mesh, feet at y 0 like the boxes.
    if (modelsEnabled()) {
      loadModel("ground_crew").then((t) => {
        mesh.geometry = bakedGeometry(t).geometry;
        geo.dispose();
      }, () => {});
    }
    const figs = [];
    for (let i = 0; i < n; i++) {
      // Scatter across the apron deck, from the hangar pads (lz −22) to the
      // revetment mouths (lz +21) — not up on the berms.
      const lx = (this.h(base.team * 100 + i, 8601) - 0.5) * 150;
      const lz = -20 + this.h(base.team * 100 + i, 8602) * 38;
      const p = this._lw(af, lx, lz);
      figs.push({
        home: { x: p.x, z: p.z },
        pos: new THREE.Vector3(p.x, af.y, p.z),
        phase: this.h(base.team * 100 + i, 8603) * TAU,
        flee: 0, fleeDir: this.h(base.team * 100 + i, 8604) * TAU,
      });
    }
    base.crew = { mesh, figs, y: af.y };
  }

  _updateBase(dt, base, sky, probes, wind) {
    // Tower dusk lighting + beacon blink.
    if (base.tower) {
      const night = sky?.nightK ?? 0;
      base.tower.lit.visible = night > 0.5;
      base.tower.beacon.visible = this.t % 2 < 1;
    }
    // Windsock points downwind, inflates with speed.
    if (base.windsock && wind) {
      const spd = Math.hypot(wind.x, wind.z);
      const yaw = Math.atan2(-wind.z, wind.x);
      // Droop below ~2 m/s, straight out by ~8 m/s.
      const droop = -clamp01(1 - spd / 8) * 1.2;
      const infl = 0.4 + 0.6 * clamp01(spd / 8);
      for (const s of base.windsock.socks) {
        s.rotation.set(0, yaw, droop);
        s.scale.set(1, infl, infl);
      }
    }
    // Ground crew: idle wander, scatter when buzzed.
    const crew = base.crew;
    if (crew) {
      for (const f of crew.figs) {
        if (f.flee > 0) f.flee -= dt;
        for (const p of probes) {
          const dx = p.x - f.pos.x, dz = p.z - f.pos.z;
          if (p.spd > 55 && (p.y - base.ground) < 40 && dx * dx + dz * dz < 90 * 90) {
            f.flee = 8;
            f.fleeDir = Math.atan2(f.pos.z - p.z, f.pos.x - p.x);
            break;
          }
        }
        f.phase += dt;
        let tx = f.home.x, tz = f.home.z;
        if (f.flee > 0) {
          // Bolt 25 m radially away and hold there; once the scare passes the
          // eased move below drifts them back home.
          tx = f.home.x + Math.cos(f.fleeDir) * 25;
          tz = f.home.z + Math.sin(f.fleeDir) * 25;
        } else {
          // Gentle idle wander around the home spot.
          tx += Math.cos(f.phase * 0.7) * 3;
          tz += Math.sin(f.phase) * 3;
        }
        const k = 1 - Math.exp(-(f.flee > 0 ? 6 : 1.5) * dt);
        f.pos.x += (tx - f.pos.x) * k;
        f.pos.z += (tz - f.pos.z) * k;
        f.pos.y = crew.y; // stay on the apron deck
      }
      let idx = 0;
      for (const f of crew.figs) {
        this.dummy.position.copy(f.pos);
        this.dummy.rotation.set(0, f.flee > 0 ? f.fleeDir : f.phase, 0);
        // Arms-up bob while fleeing (a quick vertical jiggle).
        this.dummy.position.y += f.flee > 0 ? Math.abs(Math.sin(this.t * 12)) * 0.4 : 0;
        this.dummy.updateMatrix();
        crew.mesh.setMatrixAt(idx++, this.dummy.matrix);
      }
      touchInstances(crew.mesh.instanceMatrix, 0, idx);
    }
    // Gull cry audio when a flock is fleeing near the listener is handled in
    // update() globally.
  }

  // ==========================================================================
  // Frame update
  // ==========================================================================
  update(dt, svrT, sky, probes = [], wind = null) {
    if (typeof document !== "undefined" && document.hidden) return;
    // svrT is the shared server clock (seconds); until it's known, run on local
    // time so the scene still lives.
    this.t = svrT;
    this._updateBoats(dt);
    this._updateLighthouse(sky);
    this._updateGulls(dt, probes);
    this._updateWhales(dt, probes.length ? probes[0] : null);
    for (const base of this.bases) this._updateBase(dt, base, sky, probes, wind);

    // Occasional ambient audio, distance-attenuated to the listener (probe 0 =
    // local plane). Cheap synth one-shots; skipped when audio is muted/absent.
    if (this.audio && probes.length) {
      const me = probes[0];
      // A gull cry when the local plane skims low over any fleeing flock.
      this.gullCryAt -= dt;
      if (this.gullCryAt <= 0 && this.gulls) {
        for (const a of this.gulls.anchors) {
          if (a.flee > 5.4) {
            const d = Math.hypot(me.x - a.x, me.z - a.z);
            if (d < 300) {
              this.audio.gull?.((me.x - a.x) / 300);
              this.gullCryAt = 1.5;
            }
            break;
          }
        }
      }
    }
  }
}
